import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAgentVerificationDecisionContext } from '../../../useAgentVerificationDecisionContext/useAgentVerificationDecisionContext'

import { ErrorView, Loader, PercentButton } from '@percent/admin-dashboard/common/components'
import { useMutation, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { ReactComponent as CloseIcon } from '@percent/admin-dashboard/common/assets/images/close.svg'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Box, Divider, Modal, Text, Typography } from '@percent/lemonade'
import styles from './AgentVerificationApproveModal.module.scss'
import { AgentVerificationApprovedInformation } from './agentVerificationApprovedInformation/AgentVerificationApprovedInformation'
import { AgentVerificationApproveModalProps } from './AgentVerificationApproveModal.types'

export function AgentVerificationApproveModal({
  agentFullName,
  organisationName,
  refresh
}: AgentVerificationApproveModalProps) {
  const { t } = useTranslation()
  const { setOpenedModal, isApproveModalOpened } = useAgentVerificationDecisionContext()
  const [isSuccess, setIsSuccess] = useState(false)
  const { agentVerificationService } = useServices()
  const { id } = useRequiredParams()

  const [{ isLoading, errorMessage }, { apiFunc: approveFunc }] = useMutation(
    agentVerificationService.approveAgentVerification,
    () => {
      setIsSuccess(true)
    }
  )

  const handleApprove = () => {
    approveFunc({ id })
  }

  const handleCloseModalAfterApprove = () => {
    refresh()
    setOpenedModal(undefined)
    setIsSuccess(false)
  }

  return (
    <Modal
      open={isApproveModalOpened}
      onClose={() => setOpenedModal(undefined)}
      aria-labelledby="agent-verification-approve-modal"
    >
      {isSuccess ? (
        <AgentVerificationApprovedInformation
          agentFullName={agentFullName}
          handleClick={handleCloseModalAfterApprove}
        />
      ) : (
        <Box className={styles.wrapper}>
          <Box className={styles.titleWrapper}>
            <Text className={styles.title}>{t('typography.approveAgent')}</Text>
            <CloseIcon onClick={() => setOpenedModal(undefined)} />
          </Box>
          <Divider className={styles.divider} />
          <Box className={styles.textWrapper}>
            <Typography variant="large" className={styles.bodyText}>
              <strong>{agentFullName}&apos;s </strong>
              {t('typography.approve.partOne')} <strong>{organisationName} </strong>
              {t('typography.approve.partTwo')}
            </Typography>
          </Box>
          {errorMessage && <ErrorView errorMessage={errorMessage} />}
          {isLoading ? (
            <Loader />
          ) : (
            <Box className={styles.buttonWrapper}>
              <PercentButton title={t('button.approve')} onClick={handleApprove} />
              <PercentButton
                title={t('button.cancel')}
                onClick={() => setOpenedModal(undefined)}
                textColour="secondaryTextColour"
                outlined
              />
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}
